import { HStack, PageContainer, PageHeader, Spread, Text, VStack } from '@/components/Atoms'
import { Card, CardContent } from '@/components/Card'
import { TableHeader } from '@/components/DataTable/CellContent/TableHeader'
import { ClientDataTable } from '@/components/DataTable/ClientDataTable'
import {
  useMassClrPendingConfirmations,
  type MassClrPendingConfirmationRow,
} from '@/hooks/queries/credit/usePendingConfirmations'
import { useLocalizedDate } from '@/hooks/useLocalizedDate'
import { NO_DATA_AVAILABLE_SYMBOL } from '@/lib/utils/constants'
import { cn } from '@repo/ui/lib/utils'
import { createFileRoute, Link } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { TriangleAlertIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

export const Route = createFileRoute(
  '/_auth/_modules/credit/credit-limit-reviews/mass/pending-confirmations/',
)({
  component: RouteComponent,
})

/** `ClientDataTable` rows carry an optional unsaved flag; never set here. */
type RunRow = MassClrPendingConfirmationRow & { isUnsaved?: boolean }

/**
 * Approved mass reviews with accounts the daily SAP extract has not yet been seen applying. One row
 * per run with how its accounts stand; the run's own page lists them. Red when any account came
 * back from the extract with values other than the approved ones.
 */
function RouteComponent() {
  const { t } = useTranslation(['credit', 'common'])
  const { formatDateWithTime } = useLocalizedDate()
  const query = useMassClrPendingConfirmations()

  const count = (value: number, tone?: 'destructive' | 'muted') => (
    <Text
      as="span"
      className={cn(
        'font-mono',
        tone === 'destructive' && value > 0 && 'text-destructive font-semibold',
      )}
      tone={tone === 'muted' && value === 0 ? 'muted' : undefined}
    >
      {value}
    </Text>
  )

  const rows: RunRow[] = query.data?.results ?? []

  const columns: ColumnDef<RunRow>[] = [
    {
      id: 'run',
      accessorKey: 'reference',
      header: ({ column }) => <TableHeader column={column} sortable />,
      cell: ({ row }) => (
        <Link
          to="/credit/credit-limit-reviews/mass/$runId/pending-confirmations"
          params={{ runId: String(row.original.runId) }}
          className="underline-offset-4 hover:underline"
        >
          <HStack gap="xs" align="center">
            {row.original.mismatchCount > 0 && (
              <TriangleAlertIcon className="text-destructive size-4 shrink-0" />
            )}
            <span className="font-mono">{row.original.reference}</span>
            <Text as="span" tone="muted">
              {row.original.name}
            </Text>
          </HStack>
        </Link>
      ),
      enableHiding: false,
      meta: { displayTitle: t('massClr.pendingConfirmations.columns.run') },
    },
    {
      id: 'approvedAt',
      accessorKey: 'approvedAt',
      header: ({ column }) => <TableHeader column={column} sortable />,
      cell: ({ row }) =>
        row.original.approvedAt
          ? formatDateWithTime(new Date(row.original.approvedAt))
          : NO_DATA_AVAILABLE_SYMBOL,
      meta: { displayTitle: t('massClr.pendingConfirmations.columns.approvedAt') },
    },
    {
      id: 'accountCount',
      accessorKey: 'accountCount',
      sortingFn: 'basic',
      header: ({ column }) => <TableHeader column={column} sortable align="right" />,
      cell: ({ row }) => count(row.original.accountCount),
      meta: {
        displayTitle: t('massClr.pendingConfirmations.columns.accounts'),
        cellClassName: 'text-right',
      },
    },
    {
      id: 'confirmedCount',
      accessorKey: 'confirmedCount',
      sortingFn: 'basic',
      header: ({ column }) => <TableHeader column={column} sortable align="right" />,
      cell: ({ row }) => count(row.original.confirmedCount, 'muted'),
      meta: {
        displayTitle: t('massClr.pendingConfirmations.columns.confirmed'),
        cellClassName: 'text-right',
      },
    },
    {
      id: 'pendingCount',
      accessorKey: 'pendingCount',
      sortingFn: 'basic',
      header: ({ column }) => <TableHeader column={column} sortable align="right" />,
      cell: ({ row }) => count(row.original.pendingCount, 'muted'),
      meta: {
        displayTitle: t('massClr.pendingConfirmations.columns.pending'),
        cellClassName: 'text-right',
      },
    },
    {
      id: 'mismatchCount',
      accessorKey: 'mismatchCount',
      sortingFn: 'basic',
      header: ({ column }) => <TableHeader column={column} sortable align="right" />,
      cell: ({ row }) => count(row.original.mismatchCount, 'destructive'),
      meta: {
        displayTitle: t('massClr.pendingConfirmations.columns.mismatched'),
        cellClassName: 'text-right',
      },
    },
    {
      id: 'sentCount',
      accessorFn: (row) => row.dispatch.sentCount,
      sortingFn: 'basic',
      header: ({ column }) => <TableHeader column={column} sortable align="right" />,
      cell: ({ row }) => count(row.original.dispatch.sentCount, 'muted'),
      meta: {
        displayTitle: t('massClr.pendingConfirmations.columns.sent'),
        cellClassName: 'text-right',
      },
    },
    {
      id: 'failedCount',
      accessorFn: (row) => row.dispatch.failedCount,
      sortingFn: 'basic',
      header: ({ column }) => <TableHeader column={column} sortable align="right" />,
      cell: ({ row }) => count(row.original.dispatch.failedCount, 'destructive'),
      meta: {
        displayTitle: t('massClr.pendingConfirmations.columns.failed'),
        cellClassName: 'text-right',
      },
    },
  ]

  return (
    <PageContainer maxWidth="wide">
      <Spread align="end">
        <VStack gap="xs">
          <PageHeader>{t('massClr.pendingConfirmations.pageHeader')}</PageHeader>
          <Text tone="muted" className="max-w-3xl text-pretty">
            {t('massClr.pendingConfirmations.pageDescription')}
          </Text>
        </VStack>
      </Spread>

      <Card>
        <CardContent className="pt-6">
          <ClientDataTable
            columns={columns}
            data={rows}
            isPending={query.isPending}
            error={query.error}
            rowClassName={(row) =>
              cn(
                (row.mismatchCount > 0 || row.dispatch.failedCount > 0) &&
                  'bg-destructive/5 hover:bg-destructive/10',
              )
            }
          />
        </CardContent>
      </Card>
    </PageContainer>
  )
}
